// tabs/DocumentTab.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Group, Text, SimpleGrid, Divider, ActionIcon,
} from '@mantine/core';
import { IconTrash, IconEye, IconRefresh } from '@tabler/icons-react';
import { useFacilitatorService } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { FileUploader } from '@/components/ccms-components/file-management';
import { UniversalFileViewer } from '@/components/ccms-components/file-management/components/UniversalFileViewer';
import type { ViewerFile } from '@/components/ccms-components/file-management/types';
import { getFileIcon, formatUploadedDate } from '@components/ccms-components/utils/fileDisplay';
import ConfirmActionModal from './ConfirmActionModal';
import Flex from '@/components/ui/layout/Flex';
import Tooltip from '@/components/ui/overlays/Tooltip';

// ─── Props ────────────────────────────────────────────────────────────────────
interface DocumentsTabProps {
  userId: string;
  active: boolean;
  onFacilitatorChange?: () => void;
}

// ─── Completed upload shape returned by FileUploader ──────────────────────────
interface CompletedUpload {
  url: string;
  fileName: string;
  mimeType?: string;
}

// ─── Resolve the correct document ID from whatever shape the SDK returns ──────
const resolveDocumentId = (doc: any): number | undefined =>
  doc.facilitatorDocumentID ??
  doc.facilitatorDocumentId ??
  doc.documentID            ??
  doc.documentId            ??
  doc.id;

// ─── DocumentsTab ─────────────────────────────────────────────────────────────
export default function DocumentsTab({ userId, active, onFacilitatorChange }: DocumentsTabProps) {
  const FacilitatorDocuments = useFacilitatorService();

  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading]     = useState(false);

  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction]   = useState<(() => Promise<void>) | null>(null);

  // ── Universal file viewer (preview) ────────────────────────────────────────
  const [viewerOpen, setViewerOpen]   = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  // fileSize is real since SDK 0.0.54 — it used to be hardcoded to 0.
  const viewerFiles: ViewerFile[] = useMemo(
    () =>
      documents.map((doc) => {
        const id = resolveDocumentId(doc);
        return {
          id: String(id),
          fileName: doc.name ?? `Document ${id}`,
          mimeType: doc.type ?? 'application/octet-stream',
          // Real size since SDK 0.0.54 — `FacilitatorDocumentView.fileSize` replaced a hardcoded 0.
          fileSize: doc.fileSize ?? 0,
          url: doc.url ?? undefined,
        };
      }),
    [documents]
  );

  const openPreview = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  // ── Fetch documents ────────────────────────────────────────────────────────
  const fetchDocuments = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const result = await FacilitatorDocuments.getDocuments(userId);
      setDocuments(result ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [userId, FacilitatorDocuments]);

  useEffect(() => {
    if (active) fetchDocuments();
  }, [active, fetchDocuments]);

  // ── Upload complete handler ────────────────────────────────────────────────
  const handleUploadComplete = async (files: CompletedUpload[]) => {
    try {
      await Promise.all(
        files.map((file) =>
          FacilitatorDocuments.addDocument(userId, {
            userId,
            name: file.fileName,
            url:  file.url,
            type: file.mimeType,
          } as any)
        )
      );
      await fetchDocuments();
      onFacilitatorChange?.();
      toast.success(
        'Document Added',
        `${files.length} document${files.length !== 1 ? 's' : ''} uploaded successfully.`
      );
    } catch (e) {
      console.error(e);
      toast.error('Error', 'File uploaded but failed to attach it to the facilitator.');
    }
  };

  // ── Remove document — opens confirm modal ─────────────────────────────────
  const handleRemoveDocument = (documentId: number, documentName?: string) => {
    if (documentId === undefined || documentId === null) {
      console.error('[DocumentTab] resolveDocumentId returned undefined — check doc shape.');
      toast.error('Error', 'Could not determine document ID.');
      return;
    }

    setConfirmMessage(
      `Remove this document${documentName ? ` (${documentName})` : ''} from the facilitator?`
    );

    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await FacilitatorDocuments.removeDocument(userId, documentId as any);
        await fetchDocuments();
        onFacilitatorChange?.();
        toast.warning('Document Removed', 'Document removed from facilitator.');
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove document.');
      } finally {
        setLoading(false);
      }
    });

    setConfirmOpen(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove Document"
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      <UniversalFileViewer
        opened={viewerOpen}
        onClose={() => setViewerOpen(false)}
        files={viewerFiles}
        initialIndex={viewerIndex}
        onIndexChange={setViewerIndex}
      />

      <Flex justify='right' gap='md' mb='md'>
        <ActionIcon
          variant="subtle"
          size="lg"
          onClick={fetchDocuments}
          loading={loading}
          aria-label="Refresh documents"
        >
          <IconRefresh size={16} />
        </ActionIcon>
      </Flex>

      <FileUploader
        domain="Facilitator"
        recordId={userId}
        multiple
        accept={['*/*']}
        onUploadComplete={handleUploadComplete}
        onUploadError={(err: any) => {
          console.error(err);
          toast.error('Upload Failed', err?.message ?? 'Could not upload file.');
        }}
      />

      <Divider my="md" />

      {documents.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No documents uploaded yet.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {documents.map((doc: any, index) => {
            const FileIcon   = getFileIcon(doc.type);
            const documentId = resolveDocumentId(doc);

            return (
              <Card key={documentId ?? doc.name} withBorder radius="md" padding="lg">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ overflow: 'hidden' }}>

                    <FileIcon size={28} />

                    <div style={{ overflow: 'hidden' }}>
                      <Text fw={700} truncate>
                        {doc.name ?? `Document ${documentId}`}
                      </Text>

                      <Text size="xs" c="dimmed">
                        Uploaded: {formatUploadedDate(doc.uploadedDateTime)}
                      </Text>
                    </div>
                  </Group>

                  <Group gap={4} wrap="nowrap">
                    {doc.url && (
                      <Tooltip label="Preview">
                        <ActionIcon variant="light" color="blue" onClick={() => openPreview(index)}>
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={() => handleRemoveDocument(Number(documentId), doc.name ?? undefined)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </>
  );
}